import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, TrendingUp, MapPin, DollarSign, Award } from "lucide-react";

export default function About() {
  return (
    <main data-testid="page-about" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-20 grid lg:grid-cols-12 gap-14 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow rise rise-1">Leadership · Credibility · Vision</div>
            <h1 className="font-display text-[48px] md:text-[72px] leading-[0.98] text-[var(--navy-900)] mt-6 rise rise-2">
              Led By <span className="italic">Donja Williams</span>.
            </h1>
            <p className="mt-8 max-w-xl text-[18px] leading-relaxed text-[var(--muted)] rise rise-3">
              Executive operator, ecosystem builder, and organizational
              strategist with <span className="text-[var(--navy-900)] font-semibold">20+ years</span> leading
              and working inside complex organizations — building systems and frameworks that
              strengthen operations, leadership, workforce performance, technology integration,
              and execution.
            </p>

            {/* Fortune-tier credibility chips — high-signal scan element for executive readers */}
            <div className="mt-7 flex flex-wrap gap-2.5" data-testid="about-fortune-chips">
              {["Fortune 25", "Fortune 100", "Fortune 500", "Federal Agencies", "Mission-Driven Orgs"].map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center text-[10px] tracking-[0.28em] uppercase font-bold px-3 py-1.5 border rounded-full"
                  style={{ borderColor: "rgba(201,164,90,0.55)", color: "var(--navy-900)", background: "rgba(201,164,90,0.08)" }}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 rise rise-4">
            <div className="relative aspect-[4/5] w-full max-w-md ml-auto">
              <div className="absolute -inset-2 border border-[var(--navy-900)]/15"></div>
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_signature-align-2/artifacts/j03dx5zf_305584859_6193355087359080_5498748801947020424_n.jpg"
                  alt="Donja Williams — CEO of Freedom In Me"
                  className="w-full h-full object-cover object-top"
                  data-testid="about-portrait-image"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(7,20,43,0) 55%, rgba(7,20,43,0.65) 100%)",
                  }}
                ></div>
              </div>
              <div className="absolute bottom-5 left-5 right-5 text-[var(--bone)]">
                <div className="text-[11px] tracking-[0.28em] uppercase text-[var(--gold-soft)] font-semibold">
                  CEO · Freedom In Me
                </div>
                <div className="font-display text-[22px] mt-1">Donja Williams</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hairline max-w-7xl mx-auto"></div>
      </section>

      {/* BIO — SECTION 1 */}
      <section className="bg-[var(--bone)] py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">Executive Profile</div>
            <h2 className="font-display text-[34px] md:text-[44px] leading-[1.05] text-[var(--navy-900)] mt-4">
              Operator. Strategist. Ecosystem Builder.
            </h2>
          </div>
          <div className="lg:col-span-8 text-[17px] leading-[1.75] text-[var(--navy-900)] space-y-6">
            <p>
              Donja Williams is an enterprise operations and partnerships executive known for
              translating strategy into measurable impact while building ecosystems that
              sustain both. With more than two decades of leadership experience spanning{" "}
              <span className="text-[var(--navy-700)] font-semibold">Fortune 25, Fortune 100, and Fortune 500 environments</span>,
              federal agencies, workforce development programs, and nonprofit executive
              leadership, she brings a perspective shaped by having led inside the very
              systems organizations are now trying to modernize, align, and improve.
            </p>
            <p>
              Her experience spans operations, leadership development, workforce performance,
              AI and technology integration, organizational execution, and cross-functional
              systems design. Donja is recognized for creating practical frameworks and
              operating models that help organizations reduce friction, improve alignment,
              strengthen accountability, and execute more effectively across teams,
              departments, and regions.
            </p>
            <p>
              Throughout her career, she has led complex operational environments, advised
              regional leadership teams, managed hybrid and geographically dispersed
              workforces, and overseen systems supporting thousands of daily interactions
              while maintaining strong service-level performance. Her leadership has
              contributed to operational redesign initiatives that improved execution,
              stabilized performance, reduced escalation volume, strengthened cost
              discipline, and supported scalable growth without unnecessary operational
              expansion.
            </p>
          </div>
        </div>
      </section>

      {/* LEADERSHIP EXPERIENCE — ENTERPRISE CREDIBILITY BAND
          Frames the named organizations as Donja's professional leadership history,
          NOT as Freedom In Me, LLC clients, partners, or endorsements.
          Visual treatment is intentionally restrained: grayscale wordmarks, dark
          executive ground, premium gold-accent label. */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-24 relative overflow-hidden" data-testid="about-leadership-experience">
        <div className="grain absolute inset-0 opacity-50"></div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow-light" style={{ color: "var(--accent-gold-soft)" }}>
                Leadership Experience
              </div>
              <h2 className="font-display text-[32px] md:text-[40px] leading-[1.1] mt-5 text-[var(--bone)]">
                Professional Leadership Experience<br />
                <span style={{ color: "var(--accent-gold-soft)" }}>
                  Spanning Fortune 25, Fortune 100, Fortune 500, Nonprofit,
                </span><br />
                <span className="text-[var(--bone)]">Workforce, And Operational Environments.</span>
              </h2>
              <p className="mt-7 text-[15px] leading-[1.75] text-[var(--navy-100)]/80 max-w-md">
                Two decades of operational leadership, workforce development, and
                organizational systems work inside large-scale enterprise environments —
                the very kind of complexity our methodology was built to align.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-6" style={{ color: "var(--accent-gold-soft)" }}>
                Operational Leadership Roles · A Selection
              </div>
              <ul
                data-testid="about-leadership-orgs"
                className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--navy-100)]/8"
              >
                {[
                  "Verizon Wireless",
                  "AT&T",
                  "Frontier Communications",
                  "The Home Depot",
                  "Charter Communications",
                  "Spectrum",
                ].map((org) => (
                  <li
                    key={org}
                    className="bg-[var(--navy-950)] py-7 px-5 flex items-center justify-center text-center"
                  >
                    <span
                      className="font-display text-[18px] md:text-[20px] leading-tight tracking-tight"
                      style={{ color: "rgba(255,255,255,0.80)" }}
                    >
                      {org}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[11px] leading-[1.65] text-[var(--navy-100)]/55 max-w-2xl">
                Organization names above reflect Donja Williams' professional leadership and
                operational experience over the course of her enterprise career. They are
                presented for credibility context only — not as clients, partnerships, or
                endorsements of Freedom In Me, LLC.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ACCELERATOR HIGHLIGHT */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
             style={{ background: "radial-gradient(circle, rgba(201,164,90,0.18), transparent 60%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow-light">A Parallel Practice</div>
              <h2 className="font-display text-[36px] md:text-[48px] leading-[1.05] mt-5">
                Founder & Executive Director,<br/>
                <span className="text-[var(--gold-soft)]">Freedom In Me Business Showers</span>
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[var(--navy-100)]/85 max-w-md">
                A nationally recognized accelerator supporting early-stage entrepreneurs
                through training, mentorship, strategic partnerships, and direct access to
                social capital and funding opportunities.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[var(--gold)]/15">
              {[
                { I: TrendingUp, n: "150%", t: "Year-two growth" },
                { I: MapPin,     n: "18",   t: "States with active programming" },
                { I: DollarSign, n: "$1.5M+", t: "Funding secured for entrepreneurs" },
                { I: Award,      n: "95%",  t: "Success & sustainability rate" },
              ].map((s) => (
                <div key={s.n} className="bg-[var(--navy-950)] p-8">
                  <s.I size={22} className="text-[var(--gold)]" />
                  <div className="font-display text-[44px] md:text-[56px] text-[var(--bone)] leading-none mt-4">
                    {s.n}
                  </div>
                  <div className="mt-3 text-[12px] tracking-[0.22em] uppercase text-[var(--navy-100)]/70">
                    {s.t}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-[14px] text-[var(--navy-100)]/65 max-w-3xl">
            Under her leadership, the organization secured municipal funding from the City of
            Charlotte and continues to expand programming nationally.
          </div>
        </div>
      </section>

      {/* BRIDGE — Bridging Strategy & Execution */}
      <section className="bg-[var(--bone)] py-24 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">The Practice</div>
            <h2 className="font-display text-[34px] md:text-[42px] leading-[1.05] text-[var(--navy-900)] mt-4">
              Bridging Executive Strategy With Practical Implementation.
            </h2>
          </div>
          <div className="lg:col-span-8 text-[17px] leading-[1.75] text-[var(--navy-900)] space-y-6">
            <p>
              Donja is known for bridging executive strategy with practical implementation —
              helping organizations move from ideas and initiatives into structured,
              measurable execution. She advises leaders on organizational alignment,
              workforce readiness, operational performance, partnership development, and
              sustainable growth strategies that strengthen both internal systems and
              external impact.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY QUOTE */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] relative overflow-hidden">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="eyebrow-light">Leadership Philosophy</div>
          <Quote size={48} className="text-[var(--gold-soft)] opacity-80 mt-6" />
          <blockquote className="font-display text-[28px] md:text-[40px] leading-[1.2] mt-6">
            Clarity. Accountability. Alignment. Execution. — Creating Environments Where
            <span className="italic"> People, Processes, Technology, And Strategy</span> Work
            Together Cohesively.
          </blockquote>
          <p className="mt-10 text-[15px] leading-relaxed text-[var(--navy-100)]/85 max-w-3xl">
            Our approach helps organizations reduce fear and resistance around change, AI,
            and operational transformation by simplifying complexity into clear systems,
            practical frameworks, and measurable next steps. We leverage AI and emerging
            technologies as strategic enablers — to modernize operations, improve efficiency,
            expand organizational reach, and support scalable growth in an evolving
            workforce landscape.
          </p>
          <div className="mt-10 text-[12px] tracking-[0.28em] uppercase text-[var(--gold-soft)] font-semibold">
            — Donja Williams, CEO
          </div>
        </div>
      </section>

      {/* LEGACY CLOSE */}
      <section className="bg-[var(--bone)] py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="eyebrow">The Throughline</div>
          <p className="font-display text-[28px] md:text-[36px] leading-[1.25] text-[var(--navy-900)] mt-6 max-w-4xl">
            At the core of her work is a legacy-driven commitment to building
            <span className="italic text-[var(--navy-700)]"> stronger organizations,
            stronger leaders, and stronger ecosystems.</span>
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {[
              { n: "20+", t: "Years leading large-scale operations & workforce development" },
              { n: "50+", t: "Combined years of advisory experience across the practice" },
              { n: "5",   t: "Integrated systems aligned through A.L.I.G.N.™" },
            ].map((s) => (
              <div key={s.n} className="border-l border-[rgba(10,31,68,0.15)] pl-6">
                <div className="font-display text-[56px] text-[var(--navy-900)] leading-none">{s.n}</div>
                <p className="mt-4 text-[15px] text-[var(--muted)] leading-relaxed max-w-xs">{s.t}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-4">
            <Link
              to="/align"
              data-testid="about-cta-framework"
              className="btn-primary px-7 py-3.5 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
            >
              Explore the A.L.I.G.N.™ Framework <ArrowUpRight size={14} />
            </Link>
            <a
              href="https://www.itsthefreedominme.com"
              target="_blank"
              rel="noreferrer"
              data-testid="about-cta-book-call"
              className="btn-ghost px-7 py-3.5 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
            >
              Book a Strategy Call
            </a>
            <Link
              to="/assessment"
              data-testid="about-cta-assessment"
              className="link-underline text-[12px] tracking-[0.22em] uppercase ml-2"
            >
              Take the Readiness Index
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
